// Resource queries used by several screens. All org data is keyed under
// ["org", orgId, …] so a successful mutation re-reads it from the server.
import { useQuery } from "@tanstack/react-query";
import { api, call } from "@/api/client";
import { usePaged } from "@/api/hooks";

const LIMIT = 50;

export function useClients(orgId: string, includeArchived = false) {
  return usePaged(["org", orgId, "clients", includeArchived], (cursor) =>
    call(() =>
      api.GET("/orgs/{orgId}/clients", {
        params: { path: { orgId }, query: { limit: LIMIT, includeArchived, ...(cursor ? { cursor } : {}) } },
      })
    )
  );
}

export function useBrands(orgId: string, clientId: string | undefined, includeArchived = false) {
  return usePaged(
    ["org", orgId, "client", clientId, "brands", includeArchived],
    (cursor) =>
      call(() =>
        api.GET("/orgs/{orgId}/clients/{clientId}/brands", {
          params: {
            path: { orgId, clientId: clientId ?? "" },
            query: { limit: LIMIT, includeArchived, ...(cursor ? { cursor } : {}) },
          },
        })
      ),
    Boolean(clientId)
  );
}

export function useCampaigns(orgId: string, brandId: string | undefined, includeArchived = false) {
  return usePaged(
    ["org", orgId, "brand", brandId, "campaigns", includeArchived],
    (cursor) =>
      call(() =>
        api.GET("/orgs/{orgId}/brands/{brandId}/campaigns", {
          params: {
            path: { orgId, brandId: brandId ?? "" },
            query: { limit: LIMIT, includeArchived, ...(cursor ? { cursor } : {}) },
          },
        })
      ),
    Boolean(brandId)
  );
}

export function useCreatives(orgId: string, campaignId: string | undefined) {
  return usePaged(
    ["org", orgId, "campaign", campaignId, "creatives"],
    (cursor) =>
      call(() =>
        api.GET("/orgs/{orgId}/campaigns/{campaignId}/creatives", {
          params: {
            path: { orgId, campaignId: campaignId ?? "" },
            query: { limit: LIMIT, ...(cursor ? { cursor } : {}) },
          },
        })
      ),
    Boolean(campaignId)
  );
}

export function useClient(orgId: string, clientId: string | undefined) {
  return useQuery({
    queryKey: ["org", orgId, "client", clientId],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/clients/{clientId}", { params: { path: { orgId, clientId: clientId ?? "" } } })
      ),
    enabled: Boolean(clientId),
  });
}

export function useBrand(orgId: string, brandId: string | undefined) {
  return useQuery({
    queryKey: ["org", orgId, "brand", brandId],
    queryFn: () =>
      call(() => api.GET("/orgs/{orgId}/brands/{brandId}", { params: { path: { orgId, brandId: brandId ?? "" } } })),
    enabled: Boolean(brandId),
  });
}

export function useCampaign(orgId: string, campaignId: string | undefined) {
  return useQuery({
    queryKey: ["org", orgId, "campaign", campaignId],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/campaigns/{campaignId}", { params: { path: { orgId, campaignId: campaignId ?? "" } } })
      ),
    enabled: Boolean(campaignId),
  });
}

export function useCreative(orgId: string, creativeId: string) {
  return useQuery({
    queryKey: ["org", orgId, "creative", creativeId],
    queryFn: () =>
      call(() => api.GET("/orgs/{orgId}/creatives/{creativeId}", { params: { path: { orgId, creativeId } } })),
    enabled: creativeId !== "",
  });
}

export function useVersions(orgId: string, creativeId: string) {
  return usePaged(
    ["org", orgId, "creative", creativeId, "versions"],
    (cursor) =>
      call(() =>
        api.GET("/orgs/{orgId}/creatives/{creativeId}/versions", {
          params: { path: { orgId, creativeId }, query: { limit: LIMIT, ...(cursor ? { cursor } : {}) } },
        })
      ),
    creativeId !== ""
  );
}

export function useCreativeDistributions(orgId: string, creativeId: string, includeArchived = false) {
  return usePaged(
    ["org", orgId, "creative", creativeId, "distributions", includeArchived],
    (cursor) =>
      call(() =>
        api.GET("/orgs/{orgId}/creatives/{creativeId}/distributions", {
          params: {
            path: { orgId, creativeId },
            query: { limit: LIMIT, includeArchived, ...(cursor ? { cursor } : {}) },
          },
        })
      ),
    // 7.6G.2: never request with an empty id while the version/creative is still loading
    // (GET /orgs/…/creatives//distributions → 404).
    creativeId !== ""
  );
}

export function useCampaignDistributions(orgId: string, campaignId: string) {
  return usePaged(["org", orgId, "campaign", campaignId, "distributions"], (cursor) =>
    call(() =>
      api.GET("/orgs/{orgId}/campaigns/{campaignId}/distributions", {
        params: { path: { orgId, campaignId }, query: { limit: LIMIT, ...(cursor ? { cursor } : {}) } },
      })
    )
  );
}

export function useVersion(orgId: string, versionId: string) {
  return useQuery({
    queryKey: ["org", orgId, "version", versionId],
    queryFn: () =>
      call(() => api.GET("/orgs/{orgId}/versions/{versionId}", { params: { path: { orgId, versionId } } })),
  });
}

export function useDistribution(orgId: string, distributionId: string) {
  return useQuery({
    queryKey: ["org", orgId, "distribution", distributionId],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/distributions/{distributionId}", { params: { path: { orgId, distributionId } } })
      ),
  });
}

export function useTag(orgId: string, distributionId: string) {
  return useQuery({
    queryKey: ["org", orgId, "distribution", distributionId, "tag"],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/distributions/{distributionId}/tag", { params: { path: { orgId, distributionId } } })
      ),
  });
}
